import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Bell, BellOff, X, Send, AlertCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NotificationManager({ user }) {
  const [permission, setPermission] = useState("default");
  const [showPrompt, setShowPrompt] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testingPush, setTestingPush] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    if ("Notification" in window) {
      setPermission(Notification.permission);
      checkSubscription();
    }
  }, []);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
      
      // Show prompt if not denied and not subscribed
      if (Notification.permission === "default" && !subscription) {
        // Delay showing prompt by 5 seconds after login
        setTimeout(() => {
          setShowPrompt(true);
        }, 5000);
      }
    } catch (error) {
      console.error("Error checking subscription:", error);
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, "+")
      .replace(/_/g, "/");

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribeToPushNotifications = async () => {
    setLoading(true);
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      setPermission(permission);
      console.log("Notification permission:", permission);

      if (permission !== "granted") {
        toast.error("Notification permission denied");
        setLoading(false);
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      console.log("Service worker ready:", registration);

      // Get VAPID public key from backend
      let vapidPublicKey;
      try {
        const vapidResponse = await axios.get(`${API}/notifications/vapid-public-key`, {
          withCredentials: true
        });
        vapidPublicKey = vapidResponse.data.public_key;
        console.log("VAPID key received:", vapidPublicKey ? "Yes" : "No");
      } catch (vapidError) {
        console.error("Failed to get VAPID key:", vapidError);
        toast.error("Failed to get notification config from server");
        setLoading(false);
        return;
      }

      // Subscribe to push notifications
      let subscription;
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });
        console.log("Browser subscription created:", subscription);
      } catch (pushError) {
        console.error("Push subscription failed:", pushError);
        toast.error(`Browser push subscription failed: ${pushError.message || 'Unknown error'}`);
        setLoading(false);
        return;
      }

      // Send subscription to backend
      const saveResponse = await axios.post(
        `${API}/notifications/subscribe`,
        {
          subscription: JSON.stringify(subscription),
          user_id: user.id
        },
        { withCredentials: true }
      );
      console.log("Subscription saved to backend:", saveResponse.data);

      setIsSubscribed(true);
      setShowPrompt(false);
      toast.success("Push notifications enabled!");
      
      // Fetch debug status to confirm
      await fetchDebugStatus();
    } catch (error) {
      console.error("Error subscribing to push notifications:", error);
      toast.error(`Failed to enable notifications: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const unsubscribeFromPushNotifications = async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        
        // Remove subscription from backend
        await axios.post(
          `${API}/notifications/unsubscribe`,
          { user_id: user.id },
          { withCredentials: true }
        );
      }

      setIsSubscribed(false);
      toast.success("Push notifications disabled");
    } catch (error) {
      console.error("Error unsubscribing:", error);
      toast.error("Failed to disable notifications");
    } finally {
      setLoading(false);
    }
  };

  const testPushNotification = async () => {
    setTestingPush(true);
    try {
      const response = await axios.post(
        `${API}/notifications/test-push`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        toast.success("Test notification sent! Check your device.");
      } else if (response.data.needs_resubscribe) {
        // Subscription is expired/invalid, prompt user to re-enable
        setIsSubscribed(false);
        toast.error(response.data.message || "Subscription expired. Please re-enable notifications.");
      } else {
        toast.error(response.data.message);
        console.log("Debug info:", response.data.debug);
      }
      setDebugInfo(response.data.debug);
    } catch (error) {
      console.error("Error testing notification:", error);
      toast.error("Failed to send test notification");
    } finally {
      setTestingPush(false);
    }
  };

  const fetchDebugStatus = async () => {
    try {
      const response = await axios.get(
        `${API}/notifications/debug-status`,
        { withCredentials: true }
      );
      setDebugInfo(response.data);
      console.log("Notification debug status:", response.data);
    } catch (error) {
      console.error("Error fetching debug status:", error);
    }
  };

  if (!("Notification" in window) || !("serviceWorker" in navigator)) {
    return null; // Browser doesn't support notifications
  }

  return (
    <>
      {/* Notification Prompt Card - Positioned above Polo chatbot button */}
      {showPrompt && permission === "default" && (
        <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 z-40 md:max-w-sm">
          <Card className="p-3 bg-slate-900 border border-yellow-500/30 shadow-lg">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-yellow-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium">Enable push notifications?</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button
                  onClick={subscribeToPushNotifications}
                  disabled={loading}
                  size="sm"
                  className="bg-yellow-500/100 hover:bg-yellow-600 text-black text-xs px-3 py-1 h-7"
                >
                  {loading ? "..." : "Yes"}
                </Button>
                <Button
                  onClick={() => setShowPrompt(false)}
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 text-xs px-2 py-1 h-7"
                >
                  Later
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Compact Push Notification Toggle */}
      {permission === "granted" && (
        <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-white/5">
          <div className="flex items-center gap-2">
            {isSubscribed ? (
              <Bell className="w-4 h-4 text-green-500" />
            ) : (
              <BellOff className="w-4 h-4 text-slate-400" />
            )}
            <span className="text-sm text-white">Push Notifications</span>
          </div>
          <div className="flex gap-2">
            {isSubscribed && (
              <Button
                onClick={testPushNotification}
                disabled={testingPush}
                size="sm"
                variant="ghost"
                className="text-blue-400 text-xs px-2 py-1 h-7"
              >
                {testingPush ? "..." : "Test"}
              </Button>
            )}
            <Button
              onClick={isSubscribed ? unsubscribeFromPushNotifications : subscribeToPushNotifications}
              disabled={loading}
              size="sm"
              variant={isSubscribed ? "outline" : "default"}
              className={`text-xs px-3 py-1 h-7 ${isSubscribed ? "border-slate-600 text-slate-300" : "bg-yellow-500/100 hover:bg-yellow-600 text-black"}`}
            >
              {loading ? "..." : isSubscribed ? "Off" : "On"}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
